import { memo, useState } from 'react';
import { useRouter } from 'next/router';

import { Checkbox, FieldSet, Label, LabeledControl, Legend, Toast, useToast } from '@zillow/constellation-10';

import {
  useRentalManagerUserPropertyEditWebApiHandler_hasBasketballSportCourt,
  useRentalManagerUserPropertyEditWebApiHandler_hasBocceSportCourt,
  useRentalManagerUserPropertyEditWebApiHandler_hasMultiPurposeSportCourt,
  useRentalManagerUserPropertyEditWebApiHandler_hasOtherSportCourt,
  useRentalManagerUserPropertyEditWebApiHandler_hasRacquetballSportCourt,
  useRentalManagerUserPropertyEditWebApiHandler_hasShuffleboardSportCourt,
  useRentalManagerUserPropertyEditWebApiHandler_hasSportsFieldSportCourt,
  useRentalManagerUserPropertyEditWebApiHandler_hasTennisSportCourt,
  useRentalManagerUserPropertyEditWebApiHandler_hasVolleyballSportCourt,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { useBatchMutationToast } from '../../../../hooks/useBatchMutationToast';
import { usePropertyEditMutation } from '../../../../hooks/usePropertyEditMutation';
import { getIsInputFormDisabled } from '../../utils';

import { SPORT_COURT_KEYS, SPORT_COURT_METADATA, type SportCourtKey } from '../../../../constants/KeyFeaturesConstants';

import { useAuth } from '../../../AuthProvider';

type LocalState = Record<SportCourtKey, boolean>;

const SportCourtsAmenities = (): JSX.Element | null => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const sportCourtTypes = listingDetails?.buildingAttributes?.sportCourtTypes;
  const canUpdateListing = user?.features?.canUpdateListing ?? false;

  const [localState, setLocalState] = useState<LocalState>({
    hasBasketball: sportCourtTypes?.hasBasketball ?? false,
    hasBocce: sportCourtTypes?.hasBocce ?? false,
    hasMultiPurpose: sportCourtTypes?.hasMultiPurpose ?? false,
    hasRacquetball: sportCourtTypes?.hasRacquetball ?? false,
    hasShuffleboard: sportCourtTypes?.hasShuffleboard ?? false,
    hasSportsField: sportCourtTypes?.hasSportsField ?? false,
    hasTennis: sportCourtTypes?.hasTennis ?? false,
    hasVolleyball: sportCourtTypes?.hasVolleyball ?? false,
    hasOther: sportCourtTypes?.hasOther ?? false,
  });

  const { startRequest, handleSuccess, handleError } = useBatchMutationToast({
    successMessage: 'Sport courts updated',
  });

  const isInputFormDisabled = getIsInputFormDisabled(listingDetails, canUpdateListing);

  const { mutateAsync: updateBasketball } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasBasketballSportCourt,
    skipToast: true,
    meta: { context: { api: 'hasBasketballSportCourt', details: { listingAlias } } },
  });

  const { mutateAsync: updateBocce } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasBocceSportCourt,
    skipToast: true,
    meta: { context: { api: 'hasBocceSportCourt', details: { listingAlias } } },
  });

  const { mutateAsync: updateMultiPurpose } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasMultiPurposeSportCourt,
    skipToast: true,
    meta: { context: { api: 'hasMultiPurposeSportCourt', details: { listingAlias } } },
  });

  const { mutateAsync: updateRacquetball } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasRacquetballSportCourt,
    skipToast: true,
    meta: { context: { api: 'hasRacquetballSportCourt', details: { listingAlias } } },
  });

  const { mutateAsync: updateShuffleboard } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasShuffleboardSportCourt,
    skipToast: true,
    meta: { context: { api: 'hasShuffleboardSportCourt', details: { listingAlias } } },
  });

  const { mutateAsync: updateSportsField } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasSportsFieldSportCourt,
    skipToast: true,
    meta: { context: { api: 'hasSportsFieldSportCourt', details: { listingAlias } } },
  });

  const { mutateAsync: updateTennis } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasTennisSportCourt,
    skipToast: true,
    meta: { context: { api: 'hasTennisSportCourt', details: { listingAlias } } },
  });

  const { mutateAsync: updateVolleyball } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasVolleyballSportCourt,
    skipToast: true,
    meta: { context: { api: 'hasVolleyballSportCourt', details: { listingAlias } } },
  });

  const { mutateAsync: updateOther } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasOtherSportCourt,
    skipToast: true,
    meta: { context: { api: 'hasOtherSportCourt', details: { listingAlias } } },
  });

  const mutationMap: Record<keyof LocalState, typeof updateBasketball> = {
    hasBasketball: updateBasketball,
    hasBocce: updateBocce,
    hasMultiPurpose: updateMultiPurpose,
    hasRacquetball: updateRacquetball,
    hasShuffleboard: updateShuffleboard,
    hasSportsField: updateSportsField,
    hasTennis: updateTennis,
    hasVolleyball: updateVolleyball,
    hasOther: updateOther,
  };

  const handleCheckboxChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = event.target.name as SportCourtKey;
    const checked = event.target.value === 'true';
    const previousValue = !checked;

    startRequest();
    setLocalState((prev) => ({ ...prev, [fieldName]: checked }));

    try {
      const metadata = SPORT_COURT_METADATA[fieldName];
      await mutationMap[fieldName]({
        getParams: {
          [metadata.apiKey]: checked,
          listingAlias,
        },
      });
      handleSuccess();
    } catch (err) {
      handleError();
      setLocalState((prev) => ({ ...prev, [fieldName]: previousValue }));
      const errorMessage = DatarouterError.getDisplayMessage(
        err,
        'Something went wrong, refresh the page and try again',
      );
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
    }
  };

  return (
    <FieldSet legend={<Legend>Sport courts</Legend>} disabled={isInputFormDisabled}>
      {SPORT_COURT_KEYS.map((fieldName) => {
        const metadata = SPORT_COURT_METADATA[fieldName];
        return (
          <LabeledControl
            key={`sportCourtTypes-${metadata.label}`}
            label={<Label>{metadata.label}</Label>}
            control={
              <Checkbox
                name={fieldName}
                value={String(!localState[fieldName])}
                checked={localState[fieldName]}
                onChange={handleCheckboxChange}
              />
            }
          />
        );
      })}
    </FieldSet>
  );
};

export default memo(SportCourtsAmenities);
